import {
  Text,
  IconCalendar,
  IconCode,
  IconConsole,
  IconHashtag,
  IconPlus,
  Badge,
  Button,
  Option,
  SequenceMenu,
  SetIsOpen,
  RedRed20,
  RedRed50,
  Loading,
  MagicTable,
  MagicTableCell,
  MagicTableCellEmpty,
  MagicTableEmptyState,
  MagicTableFilterConfig,
  MagicTableFilterEmptyState,
  Spinner,
  useLinkMagicTableWithSearchParams,
  useMagicTableInfiniteQuery,
  usePersistMagicTableQueryParams,
  Center,
  Flex
} from '@sequencehq/core-components'
import { CurrentUserId } from 'components/CurrentUserId/CurrentUserId'
import {
  useLoadDistinctCustomerAliasCount,
  aliasesByCustomerFilterConfig
} from 'Customer/index'
import { useLoadAllItemsQuery } from '@sequencehq/api/utils'
import { FC, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { UsageEventsSegmentedFilters } from './UsageEventsSegmentedFilters'
import {
  dashboard20240730Client,
  DashboardApi20240730
} from '@sequencehq/api/dashboard/v20240730'
import { CustomerPreviewCardPill } from 'Customer/components/CustomerPreviewCard'
import { isoTimestampIgnoringTime, HOURS } from '@sequencehq/utils/dates'
import { useQuery } from '@sequencehq/api'
import { timestampFilterOptions } from './UsageEvents'
import { UsageEventFilters, UsageEventsView } from './types'
import {
  LinkToCustomerMenuItem,
  LinkToNewCustomerMenuItem
} from 'Customer/index'
import { ProtectedButton } from 'lib/rbac'
import { useSetAtom } from 'jotai'
import { showAddNewUsageEventDrawerAtom } from './usageEvents.atoms'

const customerAliasBadge = (value: string) => <Badge>{value}</Badge>

type UsageEvent = DashboardApi20240730.UsageEvents.UsageEvent

export const UNMAPPED_ALIAS_OPTION_VALUE: string = '__SEQUENCE_UNMAPPED_ALIAS__'

export const UsageEventsMagicTable = () => {
  const setShowAddNewDrawer = useSetAtom(showAddNewUsageEventDrawerAtom)

  const { persistMagicTableQueryParams } = usePersistMagicTableQueryParams()

  const [searchParams] = useSearchParams()
  const navigate = useNavigate()

  const [eventPropertiesQuery, setEventPropertiesQuery] = useState<string>('')
  const usageEventPropertiesQuery = useQuery(
    dashboard20240730Client.getUniqueUsageEventProperties
  )
  const usageEventTypesQuery = useQuery(
    dashboard20240730Client.getUniqueUsageEventTypes
  )
  const allUsageEventProperties =
    usageEventPropertiesQuery.data?.items.map(v => ({ id: v, value: v })) ?? []
  const allUsageEventTypes = usageEventTypesQuery.data?.items

  const filteredEventProperties: Array<string> = useMemo(() => {
    return allUsageEventProperties
      .filter(({ value }) =>
        value.toLowerCase().includes(eventPropertiesQuery.toLowerCase())
      )
      .map(({ value }) => value)
  }, [allUsageEventProperties, eventPropertiesQuery])

  const distinctAliasCount = useLoadDistinctCustomerAliasCount({
    customerAlias: searchParams.get('customerAlias'),
    unmappedAliasesOnly: searchParams.get('unmappedAliasesOnly'),
    eventType: searchParams.get('eventType'),
    eventId: searchParams.get('eventId'),
    eventTimestampBefore: searchParams.get('eventTimestampBefore'),
    eventTimestampAfter: searchParams.get('eventTimestampAfter')
  })

  const aliasesInfiniteQuery = useLoadAllItemsQuery(
    dashboard20240730Client.getCustomerAliases,
    { limit: 100 },
    { staleTime: 1 * HOURS }
  )

  const aliases = useMemo(
    () => aliasesInfiniteQuery.data?.pages.flatMap(page => page.items),
    [aliasesInfiniteQuery.data]
  )

  const isLoadingAliases =
    aliasesInfiniteQuery.isPending || aliasesInfiniteQuery.hasNextPage

  const filters: MagicTableFilterConfig<UsageEventFilters>[] = [
    {
      type: 'text',
      paramName: 'eventId',
      label: 'Event ID',
      icon: IconCode
    },
    {
      type: 'multiSelect',
      paramName: 'eventType',
      options: (allUsageEventTypes ?? []).map(eventType => ({
        label: eventType,
        value: eventType
      })),
      format: value => <Text>{value}</Text>,
      label: 'Event type',
      icon: IconConsole
    },
    {
      ...aliasesByCustomerFilterConfig(aliases),
      paramName: 'customerName',
      paramNameAliasOf: 'customerAlias',
      disabled: isLoadingAliases
    },
    {
      type: 'multiSelect',
      paramName: 'customerAlias',
      options: [
        {
          label: 'Not mapped',
          value: UNMAPPED_ALIAS_OPTION_VALUE
        },
        ...(aliases ?? []).map(({ value }) => ({
          label: value,
          value
        }))
      ],
      format: value => {
        const alias = (aliases ?? []).find(
          ({ value: customerAlias }) => customerAlias === value
        )
        const aliasValue = alias?.value ?? '-'
        return customerAliasBadge(aliasValue)
      },
      label: 'Customer alias',
      icon: IconHashtag,
      optionsSortFn: (a: Option, b: Option) => {
        /**
         * Make sure the 'unmapped alias' option always appears at the top of the list
         */
        if (a.value === UNMAPPED_ALIAS_OPTION_VALUE) {
          return -1
        }

        if (b.value === UNMAPPED_ALIAS_OPTION_VALUE) {
          return 1
        }

        return a.label.localeCompare(b.label)
      },
      optionDisabledFn: (activeFilters, option) => {
        /**
         * Prevent the 'unmapped alias' option from being selected if a real alias option is selected,
         * and vice versa
         */
        const customerAliasFilter = activeFilters.find(
          activeFilter =>
            activeFilter.type === 'multiSelect' &&
            activeFilter.paramName === 'customerAlias'
        )

        if (
          !customerAliasFilter ||
          customerAliasFilter.type !== 'multiSelect'
        ) {
          return false
        }

        if (option.value === UNMAPPED_ALIAS_OPTION_VALUE) {
          return (
            customerAliasFilter.values.length > 0 &&
            !customerAliasFilter.values.includes(UNMAPPED_ALIAS_OPTION_VALUE)
          )
        }

        return customerAliasFilter.values.includes(UNMAPPED_ALIAS_OPTION_VALUE)
      },
      setQueryParamsFn: (thisFilter, activeFilter, updatedParams) => {
        if (activeFilter.type !== 'multiSelect') {
          return
        }

        if (activeFilter.values.includes(UNMAPPED_ALIAS_OPTION_VALUE)) {
          updatedParams.set('unmappedAliasesOnly', 'true')
          return
        }

        updatedParams.set(thisFilter.paramName, activeFilter.values.join(','))
      }
    },
    {
      type: 'date',
      paramName: 'eventTimestamp',
      paramNameBefore: 'eventTimestampBefore',
      paramNameAfter: 'eventTimestampAfter',
      optionsBefore: timestampFilterOptions,
      optionsAfter: timestampFilterOptions,
      format: (_, label) => label,
      label: 'Timestamp',
      stringifyDate: (date: Date) => {
        return isoTimestampIgnoringTime(date, 'start')
      },
      stringifyDateBefore: (date: Date) =>
        isoTimestampIgnoringTime(date, 'end'),
      stringifyDateAfter: (date: Date) =>
        isoTimestampIgnoringTime(date, 'start'),
      icon: IconCalendar
    },
    {
      type: 'toggle',
      paramName: 'unmappedAliasesOnly',
      label: 'unmappedAliasesOnly',
      /**
       * NOTE @ihutc
       * To prevent a filter query param from being removed by MagicTable, we need to add it as a filter here.
       * Because we want to use this filter param in another way, and not through the default `toggle`
       * behaviour which renders in the 'View settings' menu, the `hidden` prop is set to true to hide it.
       * More info in this PR: https://github.com/SequenceHQ/sequence-web/pull/4185
       */
      hidden: true,
      restoreActiveFilterFn: (dispatch, thisFilter, queryParamValue) => {
        if (thisFilter.paramName === 'unmappedAliasesOnly') {
          dispatch({
            type: 'bulkSetMultiSelect',
            paramName: 'customerAlias',
            values: [UNMAPPED_ALIAS_OPTION_VALUE]
          })

          return
        }

        dispatch({
          type: 'switchToggleOption',
          paramName: thisFilter.paramName,
          value: queryParamValue === 'true'
        })
      }
    }
  ]

  const { activeFilters, onChangeActiveFilters, sortBy, onChangeSortBy } =
    useLinkMagicTableWithSearchParams(filters)

  const { infiniteQuery } = useMagicTableInfiniteQuery(
    dashboard20240730Client.getUsageEvents,
    filters,
    activeFilters,
    sortBy,
    undefined,
    undefined,
    undefined,
    { refetchOnWindowFocus: false }
  )

  if (!allUsageEventProperties || !allUsageEventTypes) {
    return (
      <Center width="100%" height="100%">
        <Spinner />
      </Center>
    )
  }

  const additionalToolbarComponentsV3 = {
    additionalToolbarComponentsV3: {
      start: <UsageEventsSegmentedFilters />
    }
  }

  return (
    <CurrentUserId>
      {userId => (
        <MagicTable<UsageEventsView>
          activeFilters={activeFilters}
          onChangeActiveFilters={onChangeActiveFilters}
          sortBy={sortBy}
          onChangeSortBy={onChangeSortBy}
          entityNamePlural="usage events"
          sequenceUserId={userId}
          infiniteQuery={infiniteQuery}
          enableCopyResults
          additionalStatusBarComponents={
            distinctAliasCount
              ? `${distinctAliasCount} distinct customer ${
                  distinctAliasCount === '1' ? 'alias' : 'aliases'
                }`
              : undefined
          }
          rowPath={({ id }) =>
            persistMagicTableQueryParams(`/usage-events/${id}`)
          }
          objectPropertyColumns={{
            allEventProperties: filteredEventProperties,
            isLoading: usageEventPropertiesQuery.isLoading,
            getObject: (model: UsageEvent) => model.eventProperties,
            searchFunc: (query: string) => {
              setEventPropertiesQuery(query)

              return allUsageEventProperties.map(({ value }) => ({
                label: value,
                value
              }))
            }
          }}
          columns={[
            {
              id: 'id',
              accessorKey: 'customerEventId',
              header: 'Event ID',
              cell: value => (
                <MagicTableCell
                  text={value.getValue<UsageEvent['customerEventId']>()}
                  showCopyButton={true}
                />
              )
            },
            {
              id: 'eventType',
              accessorKey: 'eventType',
              header: 'Event Type',
              cell: value => (
                <MagicTableCellEmpty>
                  <Badge sentiment="monospaced" textTransform="none" truncate>
                    {value.getValue<UsageEvent['eventType']>()}
                  </Badge>
                </MagicTableCellEmpty>
              )
            },
            {
              id: 'customer',
              accessorKey: 'customerAlias',
              header: 'Customer',
              cell: value => {
                const alias = value.getValue<UsageEvent['customerAlias']>()
                const customerAliasQuery = useQuery(
                  dashboard20240730Client.getCustomerByAlias,
                  {
                    alias
                  },
                  /**
                   * A 404 is a valid outcome here!
                   */
                  {
                    retry: false,
                    enabled: !!alias
                  }
                )

                if (customerAliasQuery.isPending && alias) {
                  return (
                    <Flex
                      alignItems="center"
                      justifyContent="space-between"
                      height="40px"
                      pl="6px"
                    >
                      <Loading />
                    </Flex>
                  )
                }

                if (!customerAliasQuery.data) {
                  return (
                    <MagicTableCell
                      text="Alias not mapped to Customer"
                      borderColor={RedRed20}
                      textColor={RedRed50}
                    />
                  )
                }

                return (
                  <Flex
                    alignItems="center"
                    justifyContent="space-between"
                    height="40px"
                    pl="6px"
                  >
                    <CustomerPreviewCardPill
                      customerId={customerAliasQuery.data.id}
                      variant="ghost"
                    />
                  </Flex>
                )
              }
            },
            {
              id: 'eventTimestamp',
              accessorKey: 'eventTimestamp',
              header: 'Timestamp',
              cell: value => (
                <MagicTableCell
                  text={value.getValue<UsageEvent['eventTimestamp']>()}
                />
              ),
              enableSorting: true
            },
            {
              id: 'customerAlias',
              accessorKey: 'customerAlias',
              header: 'Customer Alias',
              cell: value => (
                <MagicTableCell
                  text={value.getValue<UsageEvent['customerAlias']>()}
                />
              ),
              enableSorting: false,
              hiddenByDefault: true
            }
          ]}
          filters={filters}
          emptyStateUnfiltered={entityNamePlural => (
            <MagicTableEmptyState
              data-testid="usageEvents.emptyState"
              entityNamePlural={entityNamePlural}
              imageSrc="/img/empty/gauge.png"
              learnMoreText="Usage events are the units in which product usage is measured"
              learnMoreHref="https://docs.sequencehq.com/metrics-events/usage-events"
              buttonNew={
                <ProtectedButton permission="org.usage:create">
                  <Button
                    variant="primary"
                    leadingIcon={<IconPlus color="inherit" />}
                    onClick={() => {
                      setShowAddNewDrawer(true)
                    }}
                    data-testid="TopNavigation.add-new"
                  >
                    New usage event
                  </Button>
                </ProtectedButton>
              }
            />
          )}
          emptyStateFiltered={(entityNamePlural, clearFilters) => (
            <MagicTableFilterEmptyState
              entityNamePlural={entityNamePlural}
              entityIcon={IconConsole}
              actionClearFilters={clearFilters}
              actionViewAll={() => navigate('/usage-events')}
              variant="TABLE"
            />
          )}
          kebabMenu={(model: UsageEvent, props) => (
            <UsageEventKebabMenu model={model} {...props} />
          )}
          {...additionalToolbarComponentsV3}
        />
      )}
    </CurrentUserId>
  )
}

const UsageEventKebabMenu: FC<{
  model: UsageEvent
  [key: string]: unknown
}> = ({ model, ...props }) => {
  const customerAliasQuery = useQuery(
    dashboard20240730Client.getCustomerByAlias,
    { alias: model.customerAlias },
    { retry: false, enabled: !!model.customerAlias }
  )

  const isLinked = !!customerAliasQuery.data
  if (isLinked) return null

  return (
    <SequenceMenu
      data-testid={`usageEvents.${model.id}.menu`}
      buttonSize="small"
      alignment="bottom-right"
      items={[
        ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
          <LinkToCustomerMenuItem
            key="link-to-customer"
            testIdPrefix="usageEvents"
            eventId={model.id}
            alias={model.customerAlias}
            onClose={() => setIsOpen?.(false)}
            onSuccess={() => setIsOpen?.(false)}
          />
        ),
        ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
          <LinkToNewCustomerMenuItem
            key="link-to-new-customer"
            testIdPrefix="usageEvents"
            eventId={model.id}
            alias={model.customerAlias}
            onClose={() => setIsOpen?.(false)}
          />
        )
      ]}
      {...props}
    />
  )
}
